// IndexedDB-backed drafts adapter with namespacing: {tenantId}:{userId}:{projectId}:{plotId}
// Falls back to localStorage sentinel when available; handles large payloads transparently.

export interface IdbDraftRecord {
  header: {
    schemaVersion: number;
    byteLength: number;
    updatedAt: number;
  };
  payload: any;
}

const DB_NAME = 'lumiplot-drafts';
const STORE_NAME = 'drafts';
const SCHEMA_VERSION = 1;

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    try {
      const req = indexedDB.open(DB_NAME, SCHEMA_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      };
      req.onerror = () => reject(req.error);
      req.onsuccess = () => resolve(req.result);
    } catch (e) {
      reject(e);
    }
  });
}

export function makeDraftKey(tenantId: string | null | undefined, userId: string | null | undefined, projectId: string | null | undefined, plotId: string): string {
  const t = tenantId || 't0';
  const u = userId || 'u0';
  const p = projectId || 'p0';
  return `${t}:${u}:${p}:${plotId}`;
}

export async function idbGetDraft(key: string): Promise<IdbDraftRecord | null> {
  try {
    const db = await openDb();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(key);
      req.onerror = () => resolve(null);
      req.onsuccess = () => {
        resolve((req.result as IdbDraftRecord) || null);
      };
    });
  } catch {
    return null;
  }
}

export async function idbPutDraft(key: string, payload: any): Promise<{ ok: true; bytes: number } | { ok: false; reason: string } > {
  try {
    const db = await openDb();
    const json = JSON.stringify(payload);
    const bytes = new Blob([json]).size;
    const rec: IdbDraftRecord = {
      header: { schemaVersion: SCHEMA_VERSION, byteLength: bytes, updatedAt: Date.now() },
      payload: JSON.parse(json),
    };
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const req = store.put(rec, key);
      req.onerror = () => reject(req.error);
      req.onsuccess = () => resolve();
    });
    try { localStorage.setItem(`lumiplot:sentinel:${key}`, String(rec.header.updatedAt)); } catch {}
    return { ok: true, bytes } as const;
  } catch (e: any) {
    const msg = String(e?.message || e || '');
    if (/quota|storage/i.test(msg)) return { ok: false, reason: 'quota_exceeded' } as const;
    if (/not allowed|denied|blocked|security/i.test(msg)) return { ok: false, reason: 'blocked' } as const;
    return { ok: false, reason: 'failed' } as const;
  }
}

export async function idbDeleteDraft(key: string): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const req = store.delete(key);
      req.onerror = () => resolve();
      req.onsuccess = () => resolve();
    });
  } catch {}
  try { localStorage.removeItem(`lumiplot:sentinel:${key}`); } catch {}
}


